import type { MaxDiffScreen, Option } from "../types";

/*
  Format spec (one pick per line):

    Most {metric}: {best for screen 1}
    Least {metric}: {worst for screen 1}
    Most {metric}: {best for screen 2}
    Least {metric}: {worst for screen 2}
    ...

  Each picked option's text occupies a single line prefixed by its label.
  Picks within a screen and across screens use the same separator, so the
  parser only needs to split on `\n` and match label prefixes — no risk
  of an option's text containing the delimiter (any internal newlines in
  option text are stripped when formatting).

  Producer/consumer chain:
    1. `formatMaxDiffAnswer` emits the lines for one screen.
    2. `summarize.ts` joins each submitted screen's output with
      `MAX_DIFF_LINE_SEPARATOR` into `aspect_answers.answer`.
    3. `parseMaxDiffAnswer` inverts (1)+(2); used by
      `MaxDiffAnswerCellRenderer.tsx` to render the table cell.

  Any change here (label words, separator, sanitization rules) must stay
  consistent across writer and reader. Both live in this file so they
  cannot drift; `summarize.ts` only imports the separator to join screen
  outputs.

  Trade-off: the table cell renders by parsing `aspect_answers.answer`
  rather than the structured `MaxDiffScreen[]`. Routing structured data
  to the cell would mean threading screens through the response query,
  response types, and table row types. Not worth that churn for now, so
  we live with the round-trip and let the shared constants keep producer
  and parser in lockstep. Revisit if the format grows.
*/

// Wire format persisted in aspect_answers.answer; respondent copy uses maxdiff-most / maxdiff-least.
const MAX_DIFF_MOST_LABEL = "Most";
const MAX_DIFF_LEAST_LABEL = "Least";
export const MAX_DIFF_LINE_SEPARATOR = "\n";

export const makeMaxDiffLabels = (
  metric: string | undefined
): { mostLabel: string; leastLabel: string } => {
  const trimmed = metric?.trim() ?? "";
  return {
    mostLabel: trimmed
      ? `${MAX_DIFF_MOST_LABEL} ${trimmed}`
      : MAX_DIFF_MOST_LABEL,
    leastLabel: trimmed
      ? `${MAX_DIFF_LEAST_LABEL} ${trimmed}`
      : MAX_DIFF_LEAST_LABEL,
  };
};

// Strip any newlines/tabs from option text so each pick stays on a single
// line. Option labels are short and rarely multi-line, but pasted text can
// contain newlines that would otherwise break the line-delimited format.
const sanitizeOptionText = (text: string): string =>
  text.replace(/\s*[\r\n\t]+\s*/g, " ").trim();

// Keep saved history and reconstructed messages readable for interviewer
// and analysis context.
export const formatMaxDiffAnswer = ({
  maxDiffScreen,
  options,
  metric,
}: {
  maxDiffScreen: MaxDiffScreen;
  options: Option[];
  metric: string | undefined;
}): string => {
  const { mostLabel, leastLabel } = makeMaxDiffLabels(metric);
  const optionById = new Map(options.map((option) => [option.id, option.text]));
  const bestText = maxDiffScreen.bestPick
    ? optionById.get(maxDiffScreen.bestPick)
    : undefined;
  const worstText = maxDiffScreen.worstPick
    ? optionById.get(maxDiffScreen.worstPick)
    : undefined;

  return [
    bestText ? `${mostLabel}: ${sanitizeOptionText(bestText)}` : undefined,
    worstText ? `${leastLabel}: ${sanitizeOptionText(worstText)}` : undefined,
  ]
    .filter((part): part is string => part !== undefined)
    .join(MAX_DIFF_LINE_SEPARATOR);
};

export type MaxDiffParsedPickKind = "most" | "least";
export type MaxDiffParsedPick = {
  kind: MaxDiffParsedPickKind;
  text: string;
};

// Recover "Most"/"Least" picks regardless of metric; \b prevents matching "Mostly:".
const METRIC_AGNOSTIC_PREFIX = /^(Most|Least)\b[^:]*:/;

// Inverse of `formatMaxDiffAnswer` + `summarize.ts`'s screen join. Parses
// an `aspect_answers.answer` string (every pick on its own line) into
// structured picks. Returns null when the string doesn't match the
// expected format so callers can fall through to a raw-text fallback.
export const parseMaxDiffAnswer = (
  answer: string,
  metric: string | undefined
): MaxDiffParsedPick[] | null => {
  const trimmed = answer.trim();
  if (!trimmed) return null;

  const { mostLabel, leastLabel } = makeMaxDiffLabels(metric);
  const mostPrefix = `${mostLabel}:`;
  const leastPrefix = `${leastLabel}:`;

  const lines = trimmed
    .split(MAX_DIFF_LINE_SEPARATOR)
    .map((t) => t.trim())
    .filter(Boolean);

  const picks: MaxDiffParsedPick[] = [];
  for (const line of lines) {
    if (line.startsWith(mostPrefix)) {
      const text = line.slice(mostPrefix.length).trim();
      if (text) picks.push({ kind: "most", text });
      continue;
    }
    if (line.startsWith(leastPrefix)) {
      const text = line.slice(leastPrefix.length).trim();
      if (text) picks.push({ kind: "least", text });
      continue;
    }
    // Metric may have been renamed after recording; recover by keyword.
    const fallback = line.match(METRIC_AGNOSTIC_PREFIX);
    if (fallback) {
      const text = line.slice(fallback[0].length).trim();
      if (text) {
        picks.push({
          kind: fallback[1] === MAX_DIFF_MOST_LABEL ? "most" : "least",
          text,
        });
      }
      continue;
    }
    return null;
  }
  return picks.length > 0 ? picks : null;
};
